import React from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import { TrendingDown, TrendingUp } from '@mxenabled/mx-icons'
import { H3, P } from '@mxenabled/mxui'

import { useWidgetContainerProvider } from '../../common'
import { formatCurrency, formatPercentage } from '../../core/utils/NumberFormatting'
const secondaryLabelWidth = 70

interface TrendsMicroSpendingIncomeBoxProps {
  title: string
  totalAmount: number
  transactionType: 'spending' | 'income'
  percentage: number
  secondaryLabel: string
  shouldDisplayPercentage: boolean
}
export const TrendsMicroSpendingIncomeBox = ({
  title,
  totalAmount,
  transactionType,
  percentage,
  secondaryLabel,
  shouldDisplayPercentage,
}: TrendsMicroSpendingIncomeBoxProps) => {
  const { availableWidth } = useWidgetContainerProvider()

  const amountRef = React.useRef<HTMLSpanElement | null>(null)
  const containerRef = React.useRef<HTMLDivElement | null>(null)

  const displayPercentage = formatPercentage(Number(Math.abs(percentage)), {
    style: 'percent',
    minimumIntegerDigits: 1,
  })

  const displayTotalAmount =
    availableWidth === 288 && formatCurrency(totalAmount, '0,0.00').length > 10
      ? formatCurrency(totalAmount, '0,0')
      : formatCurrency(totalAmount, '0,0.00')

  const isGainPercentage = transactionType === 'spending' ? percentage > 0 : percentage >= 0
  const isGainPercentageColor = transactionType === 'spending' ? 'error.main' : 'success.main'
  const isLossPercentageColor = transactionType === 'spending' ? 'success.main' : 'text.secondary'

  const amountElement = amountRef?.current
  const containerElement = containerRef?.current
  const isOverflowing =
    amountElement && containerElement
      ? amountElement.scrollWidth > containerElement.clientWidth - secondaryLabelWidth
      : false
  return (
    <Stack
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '4px',
        flex: 1,
        minWidth: 0,
        px: 12,
        py: 8,
      }}
    >
      <Stack
        sx={{
          alignItems: 'center',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <P color="text.secondary" variant="caption">
          {title}
        </P>
        {shouldDisplayPercentage && (
          <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 4 }}>
            {isGainPercentage ? (
              <TrendingUp size={20} sx={{ color: isGainPercentageColor }} />
            ) : (
              <TrendingDown size={20} sx={{ color: isLossPercentageColor }} />
            )}
            <P
              sx={{
                color: isGainPercentage ? isGainPercentageColor : isLossPercentageColor,
                fontWeight: 600,
              }}
              variant="body2"
            >
              {displayPercentage}
            </P>
          </Stack>
        )}
      </Stack>
      <Stack
        ref={containerRef}
        sx={{
          alignItems: 'center',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Box
          ref={amountRef}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          <H3>{displayTotalAmount}</H3>
        </Box>
        {shouldDisplayPercentage && availableWidth >= 450 && !isOverflowing && (
          <P
            color={isGainPercentage ? isGainPercentageColor : isLossPercentageColor}
            variant="caption"
          >
            {secondaryLabel}
          </P>
        )}
      </Stack>
    </Stack>
  )
}
